import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { EnvelopeSimple, User, UploadSimple } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

/**
 * Modal che mostra al PRO i dettagli di una richiesta di integrazione documenti
 * inviata da un operator (site/desk/tech). Si apre automaticamente quando il
 * PRO clicca sulla notifica "Integrazione documenti — {servizio} — {indirizzo}"
 * nella campanella.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - request: {
 *      id, message, author_name, created_at, email_sent?, email_error?
 *    } | null
 *  - propertyId: string  (per il link "Carica documenti" → scheda immobile)
 *  - serviceName?: string  (nome del servizio, es. "APE")
 *  - propertyAddress?: string  (indirizzo immobile, es. "via Volta 48, Milano")
 *
 * Il messaggio dell'operator contiene tipicamente una lista bullet dei
 * documenti richiesti (es. "• Visura catastale • Atto di provenienza…").
 * Manteniamo whitespace-pre-line così i newline restano visibili.
 */
export function IntegrationRequestModal({ open, onClose, request, propertyId, serviceName, propertyAddress }) {
  if (!request) {
    return (
      <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
        <DialogContent className="sm:max-w-[520px]" data-testid="integration-request-modal-empty">
          <DialogHeader>
            <DialogTitle>Richiesta non trovata</DialogTitle>
            <DialogDescription>
              Non abbiamo trovato la richiesta di integrazione. Potrebbe essere stata annullata o già gestita.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={onClose} data-testid="integration-request-close">Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-[560px]" data-testid="integration-request-modal">
        <DialogHeader>
          <DialogTitle
            className="flex items-center gap-2 text-purple-800"
            data-testid="integration-request-title"
          >
            <EnvelopeSimple size={22} weight="duotone" />
            <span className="truncate">
              Integrazione documenti
              {serviceName ? ` — ${serviceName}` : ""}
              {propertyAddress ? ` — ${propertyAddress}` : ""}
            </span>
          </DialogTitle>
          <DialogDescription data-testid="integration-request-description">
            L'operatore ti ha richiesto documenti aggiuntivi per procedere con
            {serviceName ? <> la pratica <strong>{serviceName}</strong></> : " la pratica"}
            {propertyAddress ? <> relativa all'immobile in <strong>{propertyAddress}</strong></> : ""}
            .
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {/* Header con autore (senza timestamp — rimosso per pulizia visiva) */}
          <div className="flex items-center gap-3 text-sm bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
            <div className="flex items-center gap-1.5 text-slate-700">
              <User size={14} weight="fill" className="text-purple-600" />
              <span className="font-medium">{request.author_name || "Operatore"}</span>
            </div>
          </div>

          {/* Messaggio con la lista documenti */}
          <div className="border-l-4 border-purple-500 bg-purple-50 rounded-r-md px-4 py-3">
            <div className="text-[11px] uppercase tracking-wide font-semibold text-purple-700 mb-1">
              Documenti richiesti
            </div>
            <div
              className="text-sm text-slate-800 whitespace-pre-line leading-relaxed"
              data-testid="integration-request-message"
            >
              {request.message || "—"}
            </div>
          </div>

          {/* Stato email: mostriamo SOLO il warning se l'invio è fallito.
              Il messaggio di successo è stato rimosso per ridurre il rumore
              (il PRO sa già di aver ricevuto la notifica). */}
          {request.email_sent === false && (
            <div className="text-xs bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-md px-3 py-2">
              <strong>⚠️ Nota:</strong> l'email di richiesta non è stata inviata correttamente
              (motivo: {request.email_error || "sconosciuto"}). La richiesta è comunque
              tracciata qui in app.
            </div>
          )}
        </div>

        <DialogFooter className="mt-4 gap-2 flex-col sm:flex-row">
          <Button
            variant="outline"
            onClick={onClose}
            data-testid="integration-request-close"
            className="w-full sm:w-auto"
          >
            Chiudi
          </Button>
          {propertyId ? (
            <Button
              asChild
              className="ht-gradient border-0 w-full sm:w-auto"
              data-testid="integration-request-upload"
              onClick={onClose}
            >
              <Link to={`/properties/${propertyId}`}>
                <UploadSimple size={16} className="mr-2" weight="bold" />
                Carica documenti
              </Link>
            </Button>
          ) : (
            <Button
              className="ht-gradient border-0 w-full sm:w-auto"
              data-testid="integration-request-upload"
              onClick={onClose}
              disabled
            >
              <UploadSimple size={16} className="mr-2" weight="bold" />
              Carica documenti
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
